/**
 * StudyManagement Page
 *
 * Admin UI for managing site studies and their extraction templates.
 * Features:
 * - List all site studies with template status
 * - Add new studies manually
 * - Edit study details
 * - View/reset templates (for re-teaching)
 * - Track accuracy statistics
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, Button, Input } from '../components/ui';
import { PageHeader } from '../../ui';
import { useIXVerifyAuth } from '../contexts';
import api from '../../services/api';
import { getCurrentSiteId } from '../../services/api';
import type { SiteStudy, StudyTemplate } from '../hooks';

interface StudyWithTemplate extends SiteStudy {
  template?: StudyTemplate;
  template_status: 'trained' | 'untrained';
}

export function StudyManagement() {
  const { user } = useIXVerifyAuth();
  const siteId = getCurrentSiteId();

  // State
  const [studies, setStudies] = useState<StudyWithTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add study form
  const [isAddingStudy, setIsAddingStudy] = useState(false);
  const [newStudy, setNewStudy] = useState({
    protocol_id: '',
    protocol_name: '',
    sponsor_name: ''
  });
  const [addError, setAddError] = useState<string | null>(null);

  // Edit study
  const [editingStudyId, setEditingStudyId] = useState<string | null>(null);
  const [editStudy, setEditStudy] = useState({
    protocol_name: '',
    sponsor_name: ''
  });

  // Reset template confirmation
  const [confirmResetId, setConfirmResetId] = useState<string | null>(null);

  // Fetch studies
  const fetchStudies = useCallback(async () => {
    if (!siteId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/sites/${siteId}/studies`);
      setStudies(response.data);
    } catch (err: any) {
      console.error('Error fetching studies:', err);
      setError(err.response?.data?.error || 'Failed to load studies');
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchStudies();
  }, [fetchStudies]);

  // Add new study
  const handleAddStudy = async () => {
    if (!siteId || !newStudy.protocol_id.trim()) {
      setAddError('Protocol ID is required');
      return;
    }

    setAddError(null);

    try {
      await api.post(`/api/sites/${siteId}/studies`, newStudy);
      setNewStudy({ protocol_id: '', protocol_name: '', sponsor_name: '' });
      setIsAddingStudy(false);
      fetchStudies();
    } catch (err: any) {
      console.error('Error adding study:', err);
      setAddError(err.response?.data?.error || 'Failed to add study');
    }
  };

  // Update study
  const handleUpdateStudy = async (studyId: string) => {
    try {
      await api.patch(`/api/sites/${siteId}/studies/${studyId}`, editStudy);
      setEditingStudyId(null);
      fetchStudies();
    } catch (err: any) {
      console.error('Error updating study:', err);
    }
  };

  // Toggle study active status
  const handleToggleActive = async (study: StudyWithTemplate) => {
    try {
      await api.patch(`/api/sites/${siteId}/studies/${study.id}`, {
        is_active: !study.is_active
      });
      fetchStudies();
    } catch (err: any) {
      console.error('Error toggling study status:', err);
    }
  };

  // Reset template
  const handleResetTemplate = async (studyId: string) => {
    try {
      await api.delete(`/api/studies/${studyId}/template`);
      setConfirmResetId(null);
      fetchStudies();
    } catch (err: any) {
      console.error('Error resetting template:', err);
    }
  };

  // Start editing a study
  const startEditing = (study: StudyWithTemplate) => {
    setEditingStudyId(study.id);
    setEditStudy({
      protocol_name: study.protocol_name || '',
      sponsor_name: study.sponsor_name || ''
    });
  };

  // Check if user is admin
  const isAdmin = user?.role && ['admin', 'administrator', 'superadmin', 'super_admin'].includes(user.role);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">You don't have permission to manage studies.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <PageHeader
        title="Study Management"
        subtitle="Manage protocols and their AI extraction templates"
        actions={
          <Button size="sm" onClick={() => setIsAddingStudy(true)}>
            + Add Study
          </Button>
        }
      />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/20 dark:border-red-800">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Add Study Form */}
      {isAddingStudy && (
        <Card>
          <CardContent>
            <h3 className="font-semibold mb-4 text-gray-900 dark:text-gray-100">Add New Study</h3>

            {addError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 dark:bg-red-900/20 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-400">{addError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Protocol ID *"
                value={newStudy.protocol_id}
                onChange={(e) => setNewStudy(prev => ({ ...prev, protocol_id: e.target.value }))}
                placeholder="e.g., ABC-123"
              />
              <Input
                label="Protocol Name"
                value={newStudy.protocol_name}
                onChange={(e) => setNewStudy(prev => ({ ...prev, protocol_name: e.target.value }))}
                placeholder="e.g., Phase 3 Trial"
              />
              <Input
                label="Sponsor"
                value={newStudy.sponsor_name}
                onChange={(e) => setNewStudy(prev => ({ ...prev, sponsor_name: e.target.value }))}
                placeholder="e.g., Pfizer"
              />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddingStudy(false);
                  setNewStudy({ protocol_id: '', protocol_name: '', sponsor_name: '' });
                  setAddError(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleAddStudy}>
                Add Study
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Studies List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      ) : studies.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-500 mb-4 dark:text-gray-400">No studies found for this site.</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Studies will be automatically created when you verify a new protocol,
              or you can add them manually above.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {studies.map((study) => (
            <Card key={study.id} className={!study.is_active ? 'opacity-60' : ''}>
              <CardContent>
                <div className="flex items-start justify-between">
                  {/* Study Info */}
                  <div className="flex-1">
                    {editingStudyId === study.id ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {study.protocol_id}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            label="Protocol Name"
                            value={editStudy.protocol_name}
                            onChange={(e) => setEditStudy(prev => ({ ...prev, protocol_name: e.target.value }))}
                            placeholder="Protocol name"
                          />
                          <Input
                            label="Sponsor"
                            value={editStudy.sponsor_name}
                            onChange={(e) => setEditStudy(prev => ({ ...prev, sponsor_name: e.target.value }))}
                            placeholder="Sponsor name"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleUpdateStudy(study.id)}>
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingStudyId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-900 text-lg dark:text-gray-100">
                            {study.protocol_id}
                          </span>
                          <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 text-xs font-medium rounded-full ${
                            study.template_status === 'trained'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                          }`}>
                            {study.template_status === 'trained' ? 'Trained' : 'Untrained'}
                          </span>
                          {!study.is_active && (
                            <span className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              Inactive
                            </span>
                          )}
                        </div>

                        {(study.protocol_name || study.sponsor_name) && (
                          <div className="text-sm text-gray-500 mt-1 dark:text-gray-400">
                            {study.protocol_name}
                            {study.protocol_name && study.sponsor_name && ' - '}
                            {study.sponsor_name}
                          </div>
                        )}

                        {/* Template Stats */}
                        {study.template && (
                          <div className="mt-3 flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <div>
                              <span className="font-medium">{study.template.use_count || 0}</span> uses
                            </div>
                            {study.template.accuracy_rate !== null && (
                              <div>
                                <span className="font-medium">{study.template.accuracy_rate}%</span> accuracy
                              </div>
                            )}
                            <div>
                              <span className="font-medium">{study.template.manual_corrections || 0}</span> corrections
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {editingStudyId !== study.id && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(study)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={study.is_active ? 'outline' : 'primary'}
                        onClick={() => handleToggleActive(study)}
                      >
                        {study.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      {study.template_status === 'trained' && (
                        <>
                          {confirmResetId === study.id ? (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleResetTemplate(study.id)}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setConfirmResetId(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmResetId(study.id)}
                            >
                              Reset Template
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800">
        <CardContent>
          <h3 className="font-semibold text-primary-900 mb-2 dark:text-primary-300">About Study Templates</h3>
          <ul className="text-sm text-primary-800 space-y-1 dark:text-primary-300/90">
            <li>&#8226; Templates are created automatically during the first verification of a protocol</li>
            <li>&#8226; User corrections during verification improve future extraction accuracy</li>
            <li>&#8226; Reset a template if extraction quality has degraded (requires re-teaching)</li>
            <li>&#8226; Deactivating a study hides it from the protocol selection but preserves data</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default StudyManagement;
